import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ArrowLeft, Phone, Mail, ChevronRight, Clock, Users, AlertTriangle, Sparkles, Utensils, Wine, X } from 'lucide-react';
import { Toaster } from './ui/sonner';
import { CapodannoMenu, isMenuReady } from './capodanno/CapodannoMenu';
import { IncludedBox } from './capodanno/IncludedBox';
import { CapodannoTicket } from './capodanno/CapodannoTicket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_DATETIME_ISO = '2026-12-31T23:59:00';
const waLinkFor = pkg => pkg?.whatsapp_cta
  ? `https://wa.me/${pkg.whatsapp_cta.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(pkg.whatsapp_message || 'Ciao, vorrei info sul Capodanno')}`
  : null;

// ─────────────────────────────────────────────────────────────
// Animated background: falling golden confetti + champagne bubbles
// ─────────────────────────────────────────────────────────────
function CapodannoBg() {
  const confetti = useMemo(() => Array.from({ length: 50 }, (_, i) => ({
    left: `${(i * 2.03 + 1) % 100}%`,
    size: 4 + (i % 5),
    duration: 7 + (i % 6) * 1.2,
    delay: -((i * 0.6) % 7),
    rotateStart: (i * 37) % 360,
    color: ['#fbbf24', '#f59e0b', '#fde047', '#fca5a5', '#ffffff'][i % 5],
    shape: i % 3,
  })), []);
  const bubbles = useMemo(() => Array.from({ length: 24 }, (_, i) => ({
    left: `${(i * 4.3 + 2) % 100}%`,
    size: 5 + (i % 4) * 2,
    duration: 10 + (i % 5) * 2,
    delay: -((i * 1.1) % 10),
    drift: (i % 2 === 0 ? 1 : -1) * (6 + (i % 4) * 3),
  })), []);
  return (
    <div aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {/* Base radial gradient — night sky with warm glow */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 0%, rgba(251,191,36,0.22) 0%, transparent 55%), radial-gradient(ellipse at 15% 85%, rgba(220,38,38,0.18) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(168,85,247,0.14) 0%, transparent 55%), linear-gradient(180deg, #0a0514 0%, #140a1f 55%, #1a0a0a 100%)'
      }} />
      {/* Soft starfield */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.5,
        backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.5) 0, rgba(255,255,255,0.5) 1px, transparent 1px), radial-gradient(circle at 45% 75%, rgba(255,255,255,0.4) 0, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 25% 55%, rgba(255,255,255,0.5) 0, rgba(255,255,255,0.5) 1px, transparent 1px), radial-gradient(circle at 65% 10%, rgba(255,255,255,0.4) 0, rgba(255,255,255,0.4) 1px, transparent 1px)',
        backgroundSize: '400px 300px, 500px 400px, 450px 350px, 350px 280px, 420px 320px',
        animation: 'nyeStarsTwinkle 4s ease-in-out infinite',
      }} />
      {/* Falling confetti */}
      {confetti.map((c, i) => (
        <div key={`cf-${i}`} style={{
          position: 'absolute', top: '-20px', left: c.left,
          width: `${c.size}px`, height: c.shape === 2 ? `${c.size * 0.6}px` : `${c.size}px`,
          background: c.color,
          borderRadius: c.shape === 0 ? '50%' : c.shape === 1 ? '2px' : '0',
          opacity: 0.9,
          boxShadow: `0 0 ${c.size}px ${c.color}66`,
          transform: `rotate(${c.rotateStart}deg)`,
          animation: `nyeConfettiFall ${c.duration}s linear infinite, nyeConfettiSpin ${c.duration * 0.4}s linear infinite`,
          animationDelay: `${c.delay}s, ${c.delay}s`,
        }} />
      ))}
      {/* Rising champagne bubbles */}
      {bubbles.map((b, i) => (
        <div key={`b-${i}`} style={{
          position: 'absolute', bottom: '-20px', left: b.left,
          width: `${b.size}px`, height: `${b.size}px`,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(251,191,36,0.3))',
          border: '1px solid rgba(251,191,36,0.5)',
          boxShadow: `0 0 ${b.size * 1.5}px rgba(251,191,36,0.4)`,
          '--drift': `${b.drift}px`,
          animation: `nyeBubbleRise ${b.duration}s ease-in infinite`,
          animationDelay: `${b.delay}s`,
        }} />
      ))}
      {/* Vignette */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)',
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Countdown
// ─────────────────────────────────────────────────────────────
function Countdown() {
  const target = useMemo(() => new Date(EVENT_DATETIME_ISO).getTime(), []);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  const secs  = Math.floor((diff % 60000) / 1000);

  const Cell = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div
        className="font-playfair font-900 text-white leading-none tabular-nums"
        style={{
          fontSize: 'clamp(2.6rem, 7vw, 5.4rem)',
          textShadow: '0 0 30px rgba(251,191,36,0.5), 0 0 10px rgba(255,255,255,0.3)',
        }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div className="font-manrope font-700 uppercase tracking-[0.3em] text-[10px] md:text-xs mt-1" style={{ color: '#fbbf24' }}>
        {label}
      </div>
    </div>
  );
  return (
    <div className="flex items-center justify-center gap-5 md:gap-10" data-testid="capodanno-countdown">
      <Cell value={days}  label="Giorni" />
      <div className="h-14 md:h-20 w-px" style={{ background: 'rgba(251,191,36,0.3)' }} />
      <Cell value={hours} label="Ore" />
      <div className="h-14 md:h-20 w-px" style={{ background: 'rgba(251,191,36,0.3)' }} />
      <Cell value={mins}  label="Min" />
      <div className="h-14 md:h-20 w-px hidden sm:block" style={{ background: 'rgba(251,191,36,0.3)' }} />
      <div className="hidden sm:block"><Cell value={secs} label="Sec" /></div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Booking modal for a package
// ─────────────────────────────────────────────────────────────
function CapodannoBookingModal({ event, pkg, onClose }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', num_people: 2, notes: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const canSubmit = form.first_name.trim() && form.last_name.trim() && form.phone.trim().length >= 6;

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/bookings`, {
        event_id: event.id,
        package_type: pkg.type,
        package_label: pkg.label,
        package_price: pkg.price,
        num_people: parseInt(form.num_people),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
      });
      setSuccess(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante la prenotazione.');
    } finally { setLoading(false); }
  };

  return (
    <div
      data-testid="capodanno-booking-modal"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 py-8 overflow-y-auto"
      style={{ background: 'rgba(5,5,15,0.85)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(170deg, #1a0a0f 0%, #140819 60%, #0a0514 100%)',
          border: '1px solid rgba(251,191,36,0.4)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.7), 0 0 50px rgba(251,191,36,0.2)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} data-testid="close-capodanno-modal"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center z-10"
          style={{ background: 'rgba(255,255,255,0.08)', color: '#fbbf24' }}>
          <X size={18} />
        </button>

        {success ? (
          <CapodannoTicket booking={success} pkg={pkg} event={event} onClose={onClose} />
        ) : (
          <div className="p-7">
            <div className="flex items-center gap-3 mb-4">
              <span style={{ fontSize: '2rem' }}>{pkg.icon}</span>
              <div>
                <p className="font-manrope font-700 uppercase tracking-[0.25em] text-[10px]" style={{ color: '#fbbf24' }}>Prenota · 31 Dicembre</p>
                <h3 className="font-playfair text-xl font-bold text-white leading-tight">{pkg.label}</h3>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Nome *" className="input-elegant" data-testid="cap-input-name" />
                <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Cognome *" className="input-elegant" data-testid="cap-input-surname" />
              </div>
              <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Telefono *" className="input-elegant" data-testid="cap-input-phone" />
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email (opzionale)" className="input-elegant" />
              <div className="flex items-center gap-3">
                <span className="font-manrope text-sm text-gray-300">N° persone:</span>
                <button type="button" onClick={() => setForm(f => ({ ...f, num_people: Math.max(1, f.num_people - 1) }))} className="w-10 h-10 rounded-full font-bold flex items-center justify-center" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>−</button>
                <span className="font-playfair text-2xl font-bold text-white tabular-nums w-8 text-center">{form.num_people}</span>
                <button type="button" onClick={() => setForm(f => ({ ...f, num_people: Math.min(20, f.num_people + 1) }))} className="w-10 h-10 rounded-full font-bold flex items-center justify-center" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>+</button>
              </div>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Note (allergie, intolleranze…)" rows={2} className="input-elegant" style={{ resize: 'vertical' }} />
            </div>

            <div className="flex items-center justify-between mt-5 p-3 rounded-xl" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)' }}>
              <span className="font-manrope text-sm text-gray-300">Totale</span>
              <span className="font-playfair text-2xl font-bold" style={{ color: '#fbbf24' }}>€ {pkg.price * form.num_people}</span>
            </div>
            {error && <p className="text-red-400 font-manrope text-sm mt-3">{error}</p>}
            <button
              onClick={submit}
              disabled={loading || !canSubmit}
              data-testid="cap-submit-booking"
              className="w-full mt-5 py-4 rounded-full font-manrope font-800 uppercase tracking-[0.15em] text-sm transition-all flex items-center justify-center gap-2"
              style={{
                background: canSubmit ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : 'rgba(255,255,255,0.06)',
                color: canSubmit ? '#1a0a0a' : '#666',
                boxShadow: canSubmit ? '0 10px 30px rgba(251,191,36,0.4)' : 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {loading ? 'Invio...' : 'CONFERMA PRENOTAZIONE'}
              {!loading && <ChevronRight size={18} />}
            </button>
            <p className="text-gray-500 font-manrope text-xs text-center mt-3">Pagamento in loco. Prenotazione gratuita.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Deadline chip (highlights red if < 7 days left)
// ─────────────────────────────────────────────────────────────
function DeadlineChip({ deadline }) {
  if (!deadline) return null;
  const target = new Date(deadline + 'T23:59:59').getTime();
  const diffDays = Math.floor((target - Date.now()) / 86400000);
  const isUrgent = diffDays >= 0 && diffDays <= 7;
  const isPast = diffDays < 0;
  const label = new Date(deadline + 'T00:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
  const text = isPast ? 'Prenotazioni chiuse' : `Entro il ${label}`;
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-manrope font-700 text-xs ${isUrgent && !isPast ? 'animate-pulse' : ''}`}
      style={isPast ? {
        background: 'rgba(107,114,128,0.15)', border: '1px solid rgba(107,114,128,0.3)', color: '#9ca3af',
      } : isUrgent ? {
        background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.5)', color: '#fca5a5',
      } : {
        background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24',
      }}
      data-testid={`capodanno-deadline-${isUrgent ? 'urgent' : isPast ? 'past' : 'normal'}`}
    >
      {isUrgent ? <AlertTriangle size={12} /> : <Clock size={12} />}
      {text}
      {isUrgent && !isPast && <span>· {diffDays === 0 ? 'oggi!' : `${diffDays}g rimasti`}</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Package card
// ─────────────────────────────────────────────────────────────
function PackageCard({ pkg, event, seatsInfo, onBook }) {
  const isCena = pkg.type === 'capodanno_cena';
  const Icon = isCena ? Utensils : Wine;
  const now = Date.now();
  const deadlineMs = pkg.deadline ? new Date(pkg.deadline + 'T23:59:59').getTime() : null;
  const isClosed = deadlineMs !== null && now > deadlineMs;
  const bookedPeople = seatsInfo?.[pkg.type]?.people || 0;
  const remaining = pkg.max_seats ? Math.max(0, pkg.max_seats - bookedPeople) : null;
  const soldOut = pkg.max_seats && remaining === 0;

  return (
    <article
      data-testid={`capodanno-pkg-${pkg.type}`}
      className="relative rounded-3xl overflow-hidden h-full flex flex-col"
      style={{
        background: isCena
          ? 'linear-gradient(165deg, rgba(251,191,36,0.08), rgba(220,38,38,0.1) 60%, rgba(26,10,20,0.9))'
          : 'linear-gradient(165deg, rgba(168,85,247,0.08), rgba(251,191,36,0.1) 60%, rgba(10,5,20,0.9))',
        border: '1px solid rgba(251,191,36,0.3)',
        boxShadow: '0 25px 70px rgba(0,0,0,0.5), 0 0 1px rgba(251,191,36,0.4) inset',
      }}
    >
      <div className="p-7 md:p-9 flex-1 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.3), rgba(251,191,36,0.1))',
            border: '1px solid rgba(251,191,36,0.5)',
          }}>
            <Icon size={26} style={{ color: '#fbbf24' }} />
          </div>
          <div>
            <h2 className="font-playfair font-900 text-white leading-tight" style={{ fontSize: 'clamp(1.5rem, 2.4vw, 2rem)' }}>
              {pkg.label}
            </h2>
            {pkg.subtitle && <p className="text-gray-400 font-manrope text-sm mt-1">{pkg.subtitle}</p>}
          </div>
        </div>

        {/* Price */}
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className="font-playfair font-900 text-white" style={{ fontSize: 'clamp(2.2rem, 4vw, 3rem)', textShadow: '0 0 25px rgba(251,191,36,0.35)' }}>
              € {pkg.price}
            </span>
            <span className="font-manrope text-gray-400 text-sm">a persona</span>
          </div>
        </div>

        <IncludedBox items={pkg.included} pkgType={pkg.type} />

        {/* Info chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <DeadlineChip deadline={pkg.deadline} />
          {isCena && pkg.max_seats && !soldOut && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-manrope font-700 text-xs" style={{
              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac',
            }} data-testid="capodanno-seats-remaining">
              <Users size={12} /> Ancora {remaining} / {pkg.max_seats} coperti
            </div>
          )}
          {soldOut && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-manrope font-700 text-xs" style={{
              background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.5)', color: '#fca5a5',
            }}>SOLD OUT</div>
          )}
        </div>

        {pkg.info_note && (
          <p className="text-gray-300 font-manrope text-sm leading-relaxed mb-5 flex-1">{pkg.info_note}</p>
        )}

        {/* CTAs */}
        <div className="flex flex-col gap-2 mt-auto">
          <button
            onClick={() => !isClosed && !soldOut && onBook(pkg)}
            disabled={isClosed || soldOut}
            data-testid={`capodanno-cta-${pkg.type}`}
            className="group inline-flex items-center justify-center gap-3 font-manrope font-800 uppercase tracking-[0.15em] text-sm md:text-base rounded-full transition-all duration-300"
            style={{
              padding: '1rem 1.6rem',
              background: isClosed || soldOut ? 'rgba(107,114,128,0.2)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: isClosed || soldOut ? '#9ca3af' : '#1a0a0a',
              boxShadow: isClosed || soldOut ? 'none' : '0 12px 36px rgba(251,191,36,0.4)',
              cursor: isClosed || soldOut ? 'not-allowed' : 'pointer',
              opacity: isClosed || soldOut ? 0.6 : 1,
            }}
            onMouseEnter={e => { if (!isClosed && !soldOut) { e.currentTarget.style.transform = 'translateY(-2px)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {isClosed ? 'Prenotazioni chiuse' : soldOut ? 'SOLD OUT' : 'PRENOTA ORA'}
            {!isClosed && !soldOut && <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />}
          </button>
          {isCena && (
            <a
              href="#capodanno-menu"
              onClick={e => { e.preventDefault(); document.getElementById('capodanno-menu')?.scrollIntoView({ behavior: 'smooth' }); }}
              data-testid="capodanno-discover-menu"
              className="inline-flex items-center justify-center gap-2 font-manrope font-800 uppercase tracking-[0.15em] text-xs rounded-full transition-colors"
              style={{ padding: '0.8rem 1rem', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.55)', color: '#fbbf24' }}
            >
              <Utensils size={13} /> {isMenuReady(pkg.menu) ? 'Scopri il menu' : 'Menu svelato a breve'}
            </a>
          )}
          {pkg.whatsapp_cta && (
            <a
              href={waLinkFor(pkg)}
              target="_blank" rel="noopener noreferrer"
              data-testid={`capodanno-wa-${pkg.type}`}
              className="inline-flex items-center justify-center gap-2 font-manrope font-700 text-xs transition-colors rounded-full"
              style={{ padding: '0.7rem 1rem', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.4)', color: '#4ade80' }}
            >
              <Phone size={13} /> Chiedi info menu su WhatsApp
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function CapodannoPage() {
  const [event, setEvent] = useState(null);
  const [seatsInfo, setSeatsInfo] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [bookingPkg, setBookingPkg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events?club=capodanno`)
      .then(r => {
        const ev = (r.data || [])[0];
        setEvent(ev || null);
        if (ev?.id) {
          axios.get(`${API}/events/${ev.id}/package-seats`).then(s => setSeatsInfo(s.data || {})).catch(() => {});
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const refreshSeats = () => {
    if (event?.id) {
      axios.get(`${API}/events/${event.id}/package-seats`).then(s => setSeatsInfo(s.data || {})).catch(() => {});
    }
  };

  const packages = (event?.packages || []).filter(p => p.enabled !== false);
  const cenaPkg = packages.find(p => p.type === 'capodanno_cena');
  const bgutImg = event?.image_url || '/api/uploads/capodanno_bagutti_poster.webp';

  return (
    <div data-testid="capodanno-page" style={{ background: '#0a0514', minHeight: '100vh', color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <CapodannoBg />
      <Toaster position="top-right" />

      {/* Topbar */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        backdropFilter: 'blur(16px)',
        background: 'rgba(10,5,20,0.75)',
        borderBottom: '1px solid rgba(251,191,36,0.18)',
      }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <a href="/" data-testid="capodanno-back-link" className="flex items-center gap-2 text-gray-400 hover:text-white font-manrope font-600 text-sm">
            <ArrowLeft size={18} /> <span className="hidden sm:inline">La Peschiera</span>
          </a>
          <div className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: '#fbbf24' }} />
            <span className="font-playfair font-bold text-white tracking-wide text-lg">Capodanno</span>
          </div>
          <div style={{ width: '60px' }} />
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 pt-10 md:pt-14 pb-8 px-4" data-testid="capodanno-hero">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-manrope font-700 uppercase tracking-[0.4em] text-xs mb-4" style={{ color: '#fbbf24' }}>
              ◆ 31 Dicembre 2026 · La Peschiera Club ◆
            </p>
            <h1
              className="font-playfair font-900 text-white leading-[0.95] mb-6"
              style={{
                fontSize: 'clamp(2.4rem, 6vw, 5.4rem)',
                textShadow: '0 0 50px rgba(251,191,36,0.4), 0 0 20px rgba(239,68,68,0.3)',
              }}
              data-testid="capodanno-title"
            >
              Gran Veglione di Capodanno
              <br />
              <span style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #fde047 40%, #fca5a5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                con Franco Bagutti
              </span>
            </h1>
            <p className="text-gray-300 font-manrope text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              La notte più attesa dell'anno con la voce di Franco Bagutti.
              <br />Cena di gala, orchestra dal vivo e brindisi di mezzanotte.
            </p>
          </div>

          {/* Countdown */}
          <div className="rounded-3xl p-6 md:p-10 mb-14 mx-auto" style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.08), rgba(220,38,38,0.06))',
            border: '1px solid rgba(251,191,36,0.3)',
            boxShadow: '0 25px 70px rgba(0,0,0,0.5), 0 0 40px rgba(251,191,36,0.1) inset',
            maxWidth: '900px',
          }}>
            <p className="text-center font-manrope font-700 uppercase tracking-[0.4em] text-[11px] mb-5" style={{ color: '#fbbf24' }}>
              ✦ Mancano al 2027 ✦
            </p>
            <Countdown />
          </div>

          {/* Bagutti artist card + packages */}
          <div className="grid lg:grid-cols-[0.9fr_1.3fr] gap-8 items-start">
            {/* Artist poster */}
            <div
              className="relative rounded-3xl overflow-hidden"
              data-testid="capodanno-artist-card"
              style={{
                aspectRatio: '2/3',
                background: '#140819',
                border: '1px solid rgba(251,191,36,0.3)',
                boxShadow: '0 25px 70px rgba(0,0,0,0.6), 0 0 40px rgba(251,191,36,0.1)',
              }}
            >
              <img
                src={bgutImg}
                alt="Gran Veglione di Capodanno con Franco Bagutti - La Peschiera Club"
                className="w-full h-full object-cover transition-all duration-700"
                style={{ filter: 'saturate(1.1) contrast(1.05)' }}
                onError={e => { e.target.src = '/api/uploads/capodanno_bagutti_poster.webp'; }}
              />
              {/* Shine sweep */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(100deg, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%)',
                mixBlendMode: 'overlay',
              }} />
            </div>

            {/* Packages */}
            <div className="grid gap-5">
              {!loaded ? (
                <div className="flex justify-center py-16">
                  <div className="w-10 h-10 rounded-full border-2 border-amber-400/30 border-t-amber-400 animate-spin" />
                </div>
              ) : !event || packages.length === 0 ? (
                <div className="card-dark p-10 text-center">
                  <p className="font-playfair text-2xl text-white mb-2">Dettagli in arrivo</p>
                  <p className="text-gray-400 font-manrope">Prenotazioni aperte prossimamente.</p>
                </div>
              ) : (
                packages.map(pkg => (
                  <PackageCard key={pkg.type} pkg={pkg} event={event} seatsInfo={seatsInfo} onBook={setBookingPkg} />
                ))
              )}

            </div>
          </div>
        </div>
      </section>

      {event && cenaPkg && <CapodannoMenu menu={cenaPkg.menu} waLink={waLinkFor(cenaPkg)} />}

      {/* Footer */}
      <footer className="relative z-10 mt-16" style={{ background: 'rgba(5,2,10,0.7)', borderTop: '1px solid rgba(251,191,36,0.12)', padding: '36px 16px' }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
          <div className="flex items-center gap-5 text-gray-500 font-manrope text-xs">
            <a href="tel:+393518892962" className="flex items-center gap-1.5 hover:text-amber-400"><Phone size={13} /> +39 351 889 2962</a>
            <a href="mailto:info@lapeschieraclub.it" className="flex items-center gap-1.5 hover:text-amber-400"><Mail size={13} /> info@lapeschieraclub.it</a>
          </div>
          <p className="text-gray-600 font-manrope text-xs">La Peschiera Club &copy; {new Date().getFullYear()} — Valdengo (BI)</p>
        </div>
      </footer>

      {bookingPkg && event && (
        <CapodannoBookingModal
          event={event}
          pkg={bookingPkg}
          onClose={() => { setBookingPkg(null); refreshSeats(); }}
        />
      )}
    </div>
  );
}
